import {
  AdCreative,
  AdCreativeType,
  CreativeCopyPoolsPatch,
  FBCallToAction,
} from '@marketing-milk/interfaces'
import { LocalAdPreviewCreative } from 'components/ad-preview'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { DraftMedia, imageMedia, previewSingleMedia, videoMedia } from './mediaDraft'

/**
 * Which asset and which copy variant the review surface is currently showing for
 * a dynamic ad.
 *
 * A dynamic ad has no single rendering — Facebook mixes one variant from each
 * pool with one asset at delivery. Pinning one combination is what lets a
 * reviewer pick a variant that worries them, hold the rest fixed, and see the
 * result as an actual ad rather than an inventory of parts. Purely a view
 * control: never persisted, never sent to Facebook.
 */
export interface DynamicCombination {
  asset: number
  primaryText: number
  headline: number
  description: number
  callToAction: number
}

export const FIRST_COMBINATION: DynamicCombination = {
  asset: 0,
  primaryText: 0,
  headline: 0,
  description: 0,
  callToAction: 0,
}

export interface DynamicPools {
  /** Images then videos, which is the order they were uploaded in. */
  assets: DraftMedia[]
  primaryTexts: string[]
  headlines: string[]
  descriptions: string[]
  callToActions: FBCallToAction[]
}

const EMPTY_POOLS: DynamicPools = {
  assets: [],
  primaryTexts: [],
  headlines: [],
  descriptions: [],
  callToActions: [],
}

/** What Facebook has to mix, read off a saved creative. */
export function dynamicPools(creative: AdCreative): DynamicPools {
  if (!isDynamicCreative(creative)) return EMPTY_POOLS
  const config = creative.dynamicConfig
  return {
    assets: [...creative.dynamicImages.map(imageMedia), ...creative.dynamicVideos.map(videoMedia)],
    primaryTexts: config.primaryTexts ?? [],
    headlines: config.headlines ?? [],
    descriptions: config.descriptions ?? [],
    callToActions: config.callToActions ?? [],
  }
}

/** How a pool is addressed in a copy patch. */
export type CopyPoolName = keyof CreativeCopyPoolsPatch

export interface CopyPool {
  key: keyof DynamicCombination
  /** The field this pool is sent as. */
  name: CopyPoolName
  label: string
  /** Ready to read on screen; the index into this is the index into the pool. */
  values: string[]
  /** Whether the variants are a choice from a fixed list rather than free text. */
  cta?: boolean
  /** Free text that reads better in a taller box. */
  multiline?: boolean
}

const spaced = (value: string) => value.replace(/_/g, ' ')

/**
 * Every copy variant the ad can be assembled from, in the order they read down
 * the ad, as text.
 *
 * A pool with a single entry is listed like any other: it is still copy the
 * reviewer has to read, and hiding it would leave them wondering where the
 * description went. Empty pools are dropped for a reader — Facebook varies
 * nothing there, so the ad's own field is what ships and it is already on the
 * preview — but kept for an editor, who otherwise has nowhere to add the first
 * variant to an empty pool.
 */
export function copyPools(pools: DynamicPools, includeEmpty = false): CopyPool[] {
  return [
    {
      key: 'primaryText' as const,
      name: 'primaryTexts' as const,
      label: 'Primary text',
      values: pools.primaryTexts,
      multiline: true,
    },
    {
      key: 'headline' as const,
      name: 'headlines' as const,
      label: 'Headline',
      values: pools.headlines,
    },
    {
      key: 'description' as const,
      name: 'descriptions' as const,
      label: 'Description',
      values: pools.descriptions,
    },
    {
      key: 'callToAction' as const,
      name: 'callToActions' as const,
      label: 'Call to action',
      // Spaced for reading; an editor picks the raw value from the CTA list.
      values: includeEmpty ? pools.callToActions : pools.callToActions.map(spaced),
      cta: true,
    },
  ].filter(pool => includeEmpty || pool.values.length > 0)
}

const clampIndex = (index: number, length: number) =>
  Math.min(Math.max(Math.trunc(index) || 0, 0), Math.max(length - 1, 0))

/** Keep a saved selection valid when a focus refetch returns shorter pools. */
export function clampCombination(pools: DynamicPools, at: DynamicCombination): DynamicCombination {
  return {
    asset: clampIndex(at.asset, pools.assets.length),
    primaryText: clampIndex(at.primaryText, pools.primaryTexts.length),
    headline: clampIndex(at.headline, pools.headlines.length),
    description: clampIndex(at.description, pools.descriptions.length),
    callToAction: clampIndex(at.callToAction, pools.callToActions.length),
  }
}

/**
 * One pinned combination, in the shape the ad renderer reads.
 *
 * An empty pool falls back to the creative's own field, which is what the ad
 * ships with when Facebook has nothing to vary there. With no assets at all the
 * format stays `dynamic`, so the renderer shows its honest placeholder rather
 * than an empty image frame.
 */
export function pinnedCombination(
  creative: AdCreative,
  pools: DynamicPools,
  at: DynamicCombination
): LocalAdPreviewCreative {
  const pinned = clampCombination(pools, at)
  const asset = pools.assets[pinned.asset]

  return {
    ...(asset ? previewSingleMedia(asset) : { format: AdCreativeType.dynamic }),
    primaryText: pools.primaryTexts[pinned.primaryText] ?? creative.primaryText,
    linkHeadline: pools.headlines[pinned.headline] ?? creative.linkHeadline,
    linkDescription: pools.descriptions[pinned.description] ?? creative.linkDescription,
    callToAction: pools.callToActions[pinned.callToAction] ?? creative.callToAction,
    linkDisplayURL: creative.linkDisplayURL,
  }
}
